<script setup lang="ts">
import type { AudioAsset } from "@/models"
import { computed, onBeforeUnmount, ref, watch, watchEffect } from "vue"
import { useListSlideshowAudio } from "@/api"
import { Icon } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useTranslation } from "@/composables"

export type SlideshowMusicChoice = "random" | "select" | "none"

const props = defineProps<{
  eventVaultId?: number
  enabled?: boolean
  hidden?: boolean
  resetKey?: string
}>()

const musicChoice = defineModel<SlideshowMusicChoice>("choice", { default: "random" })
const selectedTrackId = defineModel<number | undefined>("trackId")

const { t } = useTranslation()
const toast = useToast()

const eventVaultId = computed(() => props.eventVaultId)
const audioQueryEnabled = computed(() => props.enabled ?? true)

const {
  data: audioTracks,
  isLoading: isLoadingAudio,
} = useListSlideshowAudio(eventVaultId, undefined, { enabled: audioQueryEnabled })

const showMusicOptions = computed(() => Boolean(audioTracks.value?.length))

const musicOptions = computed(() => [
  { value: "random" as const, label: t("slideshow_export_drawer.music_random") },
  { value: "select" as const, label: t("slideshow_export_drawer.music_select") },
  { value: "none" as const, label: t("slideshow_export_drawer.music_none") },
])

let previewAudio: HTMLAudioElement | null = null
const previewingAssetId = ref<number>()

function stopPreview() {
  previewAudio?.pause()
  previewAudio = null
  previewingAssetId.value = undefined
}

function onPreviewError(audio: HTMLAudioElement) {
  if (audio !== previewAudio)
    return

  stopPreview()
  toast.error({ description: t("slideshow_export_drawer.music_preview_error") })
}

function onPreviewToggle(track: AudioAsset) {
  const isPreviewing = previewingAssetId.value === track.assetId

  stopPreview()

  if (isPreviewing || !track.previewUrl)
    return

  const audio = new Audio(track.previewUrl)

  previewAudio = audio
  audio.addEventListener("ended", stopPreview)
  audio.addEventListener("error", () => onPreviewError(audio))
  audio.play().catch(() => onPreviewError(audio))
  previewingAssetId.value = track.assetId
}

onBeforeUnmount(stopPreview)

// Stop music preview when the picker is disabled/hidden, the track list is
// hidden, or the playing track is no longer in the list.
watchEffect(() => {
  const shouldStopMusicPreview
    = !props.enabled
      || props.hidden
      || musicChoice.value !== "select"
      || (Boolean(previewingAssetId.value) && !audioTracks.value?.some(track => track.assetId === previewingAssetId.value))

  if (shouldStopMusicPreview)
    stopPreview()
})

// Reset selection when switching slideshows.
watch(() => props.resetKey, () => {
  stopPreview()
  musicChoice.value = "random"
  selectedTrackId.value = undefined
})

defineExpose({
  stopPreview,
})
</script>

<template>
  <fieldset
    v-if="showMusicOptions && !hidden"
    class="border-0 p-0 m-0 min-w-0"
  >
    <legend class="mb-2 font-semibold">
      {{ t("slideshow_export_drawer.music_label") }}
    </legend>
    <div class="flex flex-col gap-2">
      <label
        v-for="option in musicOptions"
        :key="option.value"
        class="flex items-center gap-2"
      >
        <input
          v-model="musicChoice"
          type="radio"
          name="slideshow-video-music"
          :value="option.value"
          class="accent-primary"
          :data-test="`music-option-${option.value}`"
        >
        <span>{{ option.label }}</span>
      </label>
    </div>

    <div v-if="musicChoice === 'select'" class="mt-3 flex flex-col gap-2">
      <div v-if="isLoadingAudio" class="flex flex-col gap-2">
        <div class="gpx-skeleton w-full h-12" />
        <div class="gpx-skeleton w-full h-12" />
      </div>
      <ul v-else-if="audioTracks?.length" class="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200">
        <li v-for="track in audioTracks" :key="track.assetId">
          <label class="flex cursor-pointer items-center gap-3 p-3">
            <input
              v-model="selectedTrackId"
              type="radio"
              name="slideshow-video-track"
              :value="track.assetId"
              class="accent-primary"
              data-test="music-track-option"
            >
            <span class="min-w-0 flex-1">
              <span class="block wrap-break-word font-medium">{{ track.title }}</span>
              <span v-if="track.artists" class="block wrap-break-word text-sm text-gray-500">{{ track.artists }}</span>
            </span>
            <button
              v-if="track.previewUrl"
              type="button"
              class="shrink-0 text-primary"
              data-test="music-track-preview"
              @click.stop.prevent="onPreviewToggle(track)"
            >
              <Icon :icon="previewingAssetId === track.assetId ? 'Pause' : 'Play'" />
              <span class="sr-only">
                {{ previewingAssetId === track.assetId
                  ? t("slideshow_export_drawer.music_stop_preview", { title: track.title })
                  : t("slideshow_export_drawer.music_preview", { title: track.title }) }}
              </span>
            </button>
          </label>
        </li>
      </ul>
      <p v-if="!selectedTrackId" class="flex items-start gap-2 text-sm text-gray-500">
        <Icon icon="CircleInfo" size="sm" /> {{ t("slideshow_export_drawer.music_select_hint") }}
      </p>
    </div>
  </fieldset>
</template>
